import { withoutUndefined } from './canvasMapping/dateSettings';

// The metricIds entries another entry takes its target from. Such an entry
// only supplies that target, so isn't a bullet of its own.
export const getBulletTargetKeys = (
  content: Pick<Studio.ChartContent, 'metricIds'> &
    Partial<Pick<Studio.ChartContent, 'chart'>>,
): string[] => {
  const metricIds = content.metricIds ?? [];
  const options = content.chart?.metrics ?? {};
  return metricIds.flatMap((key) => {
    const target = options[key]?.bullet?.target;
    return target?.source === 'metric' &&
      target.key !== key &&
      metricIds.includes(target.key)
      ? [target.key]
      : [];
  });
};

// Whether a Bullet has something to measure against: a metric with a
// target, or with bands of fixed thresholds
export const hasBulletTargets = (
  content: Pick<Studio.ChartContent, 'metricIds'> &
    Partial<Pick<Studio.ChartContent, 'chart'>>,
): boolean => {
  const options = content.chart?.metrics ?? {};
  const targetKeys = getBulletTargetKeys(content);
  return (content.metricIds ?? [])
    .filter((key) => !targetKeys.includes(key))
    .some((key) => {
      const bullet = options[key]?.bullet;
      return !!bullet?.target || bullet?.bands?.mode === 'absolute';
    });
};

/**
 * Each series with its metric's bullet options (see BulletCharts), where
 * series[i] is metricIds[i]'s. A metric target becomes its series' id, and
 * that series only supplies it. Options are only written when chosen, so a
 * block saved without them rebuilds exactly.
 */
const buildBulletSeries = ({
  content,
  series,
}: {
  content: Pick<Studio.ChartContent, 'metricIds'> &
    Partial<Pick<Studio.ChartContent, 'chart'>>;
  series: V5ChartDefinitionSeries[];
}): V5ChartDefinitionSeries[] => {
  const metricIds = content.metricIds ?? [];
  const options = content.chart?.metrics ?? {};
  const targetKeys = getBulletTargetKeys(content);

  const toTarget = (
    target: Studio.BulletMetricOptions['target'],
  ): BulletCharts.Target | undefined => {
    if (!target) {
      return undefined;
    }
    if (target.source !== 'metric') {
      return target;
    }
    const targetSeries = series[metricIds.indexOf(target.key)];
    return targetSeries
      ? { source: 'series', seriesId: targetSeries.id }
      : undefined;
  };

  return series.map((s, index) => {
    const key = metricIds[index];
    if (targetKeys.includes(key)) {
      return { ...s, bullet: { isTarget: true } };
    }
    const bulletOptions = options[key]?.bullet;
    if (!bulletOptions) {
      return s;
    }
    const bullet = withoutUndefined<BulletCharts.SeriesConfig>({
      target: toTarget(bulletOptions.target),
      lowerIsBetter: bulletOptions.lowerIsBetter,
      bands: bulletOptions.bands,
    });
    return Object.keys(bullet).length > 0 ? { ...s, bullet } : s;
  });
};

// Grouped by a dimension, a bullet per term of the first metric: it and the
// series it takes its target from
export const toGroupedBulletSeries = (
  series: V5ChartDefinitionSeries[],
): V5ChartDefinitionSeries[] => {
  const first = series.find((s) => !s.bullet?.isTarget);
  if (!first) {
    return [];
  }
  const target = first.bullet?.target;
  const targetId = target?.source === 'series' ? target.seriesId : undefined;
  return series.filter((s) => s === first || s.id === targetId);
};

export default buildBulletSeries;

// A bullet's options as Studio has them, from its series (the reverse of
// buildBulletSeries): a series target is the key of the series it names.
// A series that only supplies a target has none of its own.
export const guessBulletOptions = ({
  chartDef,
  index,
  keys,
}: {
  chartDef: V5ChartDefinition;
  index: number;
  // Each series' key in the block's metricIds, in order
  keys: string[];
}): Studio.BulletMetricOptions | undefined => {
  const bullet = chartDef.series[index]?.bullet;
  if (chartDef.gadgetType !== 'bullet' || !bullet || bullet.isTarget) {
    return undefined;
  }
  const target = ((): Studio.BulletMetricOptions['target'] => {
    if (bullet.target?.source !== 'series') {
      return bullet.target;
    }
    const { seriesId } = bullet.target;
    const key = keys[chartDef.series.findIndex((s) => s.id === seriesId)];
    return key ? { source: 'metric', key } : undefined;
  })();
  const options = withoutUndefined<Studio.BulletMetricOptions>({
    target,
    lowerIsBetter: bullet.lowerIsBetter,
    bands: bullet.bands,
  });
  return Object.keys(options).length > 0 ? options : undefined;
};

// Series targets by the position of the series they name, for comparing
// charts: Studio names its series after the block, other editors (the MCP)
// their own way
export const withComparableBulletTargets = (
  chartDef: V5ChartDefinition,
): V5ChartDefinition => {
  if (chartDef.gadgetType !== 'bullet') {
    return chartDef;
  }
  return {
    ...chartDef,
    series: chartDef.series.map((s) => {
      const target = s.bullet?.target;
      if (!s.bullet || target?.source !== 'series') {
        return s;
      }
      const position = chartDef.series.findIndex(
        (other) => other.id === target.seriesId,
      );
      return {
        ...s,
        bullet: {
          ...s.bullet,
          target: { source: 'series', seriesId: `#${position}` },
        },
      };
    }),
  };
};
